import { useState } from 'react';
import { CourtDecisionType } from '../types/LegalCase';
import type { ErrorField } from '../types/ErrorField';
import type { AddCourtDecisionValue } from './useLegalCaseCourtDecisions';

// Campos com binding próprio na tela; qualquer outro 422 do backend cai no bloco residual.
const INLINE_FIELDS = new Set(['decisionType', 'decisionDate', 'summary']);

export const residualErrors = (errorFields: ErrorField[]): ErrorField[] =>
    errorFields.filter((e) => !INLINE_FIELDS.has(e.fieldName));

interface UseLegalCaseCourtDecisionFormArgs {
    onSubmit: (value: AddCourtDecisionValue) => Promise<boolean>;
    // Quando presente, o form é de um recurso: nasce com o tipo travado em APPEAL_FILED e
    // envia o vínculo para a decisão de origem. Ausente = decisão comum.
    relatedDecisionId?: string;
}

// Estado e handlers do formulário de decisão, fora do componente (diretriz do repo: sem
// função dentro de função). Centraliza o reset dos campos ao enviar com sucesso. Serve tanto
// à decisão comum quanto ao recurso (quando recebe relatedDecisionId): no recurso o tipo já
// nasce como "Recurso interposto".
export default function useLegalCaseCourtDecisionForm({
    onSubmit,
    relatedDecisionId,
}: UseLegalCaseCourtDecisionFormArgs) {
    const isAppeal = !!relatedDecisionId;
    const initialType = isAppeal ? CourtDecisionType.APPEAL_FILED : CourtDecisionType.RULING;
    const [decisionType, setDecisionType] = useState<CourtDecisionType>(initialType);
    const [decisionDate, setDecisionDate] = useState('');
    const [summary, setSummary] = useState('');

    const reset = () => {
        setDecisionType(initialType);
        setDecisionDate('');
        setSummary('');
    };

    // A data da decisão é obrigatória (o backend rejeita ausente com 422); só libera o
    // envio quando preenchida.
    const canSubmit = decisionDate.trim().length > 0;

    const submit = async () => {
        if (!canSubmit) return;
        const ok = await onSubmit({ decisionType, decisionDate, summary, relatedDecisionId });
        if (ok) reset();
    };

    return {
        isAppeal,
        decisionType, setDecisionType,
        decisionDate, setDecisionDate,
        summary, setSummary,
        canSubmit, submit, reset,
    };
}
